import { omit } from 'lodash';
import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

interface IInputProps {
  id?: string;
  type?: 'text' | 'number' | 'email' | 'password' | 'file' | 'submit';
  label?: string;
  className?: string;
  error?: string;
  placeholder?: string;
  onChange?: (value: React.ChangeEvent<HTMLInputElement>) => void;
  icon?: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
  extraInfo?: string;
  isViewMode?: boolean;
  labelClassName?: string;
  isTimes?: boolean;
}

type TInputType = IInputProps & React.ComponentProps<'input'>;

/**
 * @deprecated This component is deprecated and should not be used in new code.
 * Please migrate to the new component.
 * @link src/lib/Input/Input.tsx
 */
const Input: FunctionComponent<TInputType> = ({
  id,
  type = 'text',
  name,
  label,
  className,
  onChange,
  error,
  placeholder,
  extraInfo,
  isViewMode,
  labelClassName,
  isTimes = false,
  ...rest
}) => {
  const { t } = useTranslation('audience_edit');

  let inputStyle =
    'inline-flex w-full px-4 sm:text-sm border border-gray-300 focus:border-brand-600 focus:border focus:outline-none rounded-md placeholder-gray-400';

  // Add darker border when input has a value (to match SearchDropdown styling)
  if (rest.value && rest.value !== '') {
    inputStyle = twMerge(inputStyle, 'border-primary-700');
  }

  if (rest.icon) {
    inputStyle = twMerge(inputStyle, 'pl-10');
  }

  if (isTimes) {
    inputStyle = twMerge(inputStyle, 'pr-12');
  }

  if (error) {
    inputStyle = twMerge(inputStyle, 'border-red-500 hover:border-red-500 focus:border-red-500 outline-none');
  }

  if (rest.disabled) {
    inputStyle = twMerge(inputStyle, 'bg-gray-200 text-gray-500');
  }

  const viewModeProps = { type };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div className='inline-flex w-full flex-col text-left'>
      {label && (
        <label htmlFor={name} className={twMerge('block text-sm font-medium text-gray-700 py-2', labelClassName)}>
          {label}
        </label>
      )}
      <div className='relative w-auto rounded-md'>
        {rest.icon && (
          <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
            <rest.icon className='mr-4 size-6 shrink-0 text-gray-500' />
          </div>
        )}
        {isViewMode ? (
          <div {...viewModeProps} className={className ? twMerge(inputStyle, className) : inputStyle}>
            {rest.value}
            {isTimes && <span className='ml-1 text-sm text-gray-500'>{t('rules.times')}</span>}
          </div>
        ) : (
          <>
            <input
              {...omit(rest, 'icon')}
              id={id}
              name={name}
              type={type}
              onChange={handleChange}
              placeholder={placeholder}
              disabled={rest.disabled || isViewMode}
              className={className ? twMerge(inputStyle, className) : inputStyle}
            />
            {isTimes && (
              <div className='pointer-events-none absolute inset-y-0 right-4 flex items-center pr-3'>
                <span className='text-sm text-gray-500'>{t('rules.times')}</span>
              </div>
            )}
          </>
        )}
      </div>
      {error && <p className='mt-2 text-sm text-red-600'>{error}</p>}
      {extraInfo && !error && <p className='mt-2 text-sm text-gray-500'>{extraInfo}</p>}
    </div>
  );
};

export default Input;
